import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../api/client';
import { useBatches } from '../App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import {
  MailCheck,
  Loader2,
  CheckCircle,
  XCircle,
  AlertTriangle,
  HelpCircle,
  FileText,
  Play,
  X,
  FileUp,
  Square,
  Download,
} from 'lucide-react';

interface Project {
  id: string;
  name: string;
}

interface VerifyResult {
  email: string;
  status: string;
  reason?: string;
  score?: number;
  cached?: boolean;
  error?: string;
}

interface BatchJob {
  id: string;
  status: string;
  total_count: number;
  processed_count: number;
  cache_hits: number;
  project_name: string;
  created_at: string;
}

export default function Verify() {
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'single');
  const { activeBatches, fetchActiveBatches, addOptimisticBatch } = useBatches();
  const [prevBatchCount, setPrevBatchCount] = useState(0);

  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [loading, setLoading] = useState(true);

  // Single verification
  const [singleEmail, setSingleEmail] = useState('');
  const [singleResult, setSingleResult] = useState<VerifyResult | null>(null);
  const [singleLoading, setSingleLoading] = useState(false);

  // Batch verification - Upload state
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [parsedEmails, setParsedEmails] = useState<string[]>([]);
  const [batchStep, setBatchStep] = useState<'upload' | 'ready'>('upload');

  // Stopping batch
  const [stoppingBatchId, setStoppingBatchId] = useState<string | null>(null);

  // Batch history
  const [batches, setBatches] = useState<BatchJob[]>([]);

  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    fetchProjects();
    fetchBatches();
  }, []);

  // Sync tab with URL params
  useEffect(() => {
    const tabFromUrl = searchParams.get('tab');
    if (tabFromUrl && tabFromUrl !== activeTab) {
      setActiveTab(tabFromUrl);
    }
  }, [searchParams]);

  // Refresh history when batch completes
  useEffect(() => {
    if (prevBatchCount > 0 && activeBatches.length < prevBatchCount) {
      fetchBatches();
    }
    setPrevBatchCount(activeBatches.length);
  }, [activeBatches.length]);

  // Trigger processing for each active batch
  const processingRef = useRef(new Set<string>());
  useEffect(() => {
    if (activeBatches.length === 0) return;

    const processAll = async () => {
      for (const batch of activeBatches) {
        if (processingRef.current.has(batch.id)) continue;
        processingRef.current.add(batch.id);
        api.processBatchChunk(batch.id).finally(() => {
          processingRef.current.delete(batch.id);
        });
      }
    };

    processAll();
    const interval = setInterval(processAll, 5000);
    return () => clearInterval(interval);
  }, [activeBatches]);

  const fetchProjects = async () => {
    try {
      const { projects } = await api.getProjects();
      setProjects(projects);
      // No default selection — user must choose explicitly
    } catch {
      // Silently fail
    } finally {
      setLoading(false);
    }
  };

  const fetchBatches = async () => {
    try {
      const { batches } = await api.getBatches();
      setBatches(batches);
    } catch {
      // Silently fail
    }
  };

  const handleSingleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedProject || !singleEmail) return;

    setSingleLoading(true);
    setSingleResult(null);

    try {
      const result = await api.verifySingle(singleEmail.trim(), selectedProject);
      setSingleResult(result);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Verification failed';
      setSingleResult({ email: singleEmail, status: 'error', error: message });
    } finally {
      setSingleLoading(false);
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadedFile(file);
    const reader = new FileReader();
    reader.onload = (event) => {
      const content = event.target?.result as string;
      const emails = content
        .split(/[\n,;]/)
        .map((e) => e.trim())
        .filter((e) => e && e.includes('@'));

      // Remove duplicates
      const uniqueEmails = [...new Set(emails)];
      setParsedEmails(uniqueEmails);
      setBatchStep('ready');
    };
    reader.readAsText(file);

    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleCancelUpload = () => {
    setUploadedFile(null);
    setParsedEmails([]);
    setBatchStep('upload');
  };

  const handleStartBatch = async () => {
    if (!selectedProject || parsedEmails.length === 0) return;

    const projectName = projects.find((p) => p.id === selectedProject)?.name || '';
    const emailCount = parsedEmails.length;

    try {
      const result = await api.createBatchJob(parsedEmails, selectedProject);

      // Show batch immediately in History
      addOptimisticBatch({
        id: result.batchId,
        projectName,
        status: 'pending',
        totalCount: emailCount,
        processedCount: 0,
        cacheHits: 0,
        createdAt: new Date().toISOString(),
        progress: 0,
      });

      // Reset upload state and switch to history
      setUploadedFile(null);
      setParsedEmails([]);
      setBatchStep('upload');
      setActiveTab('history');
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to create batch job';
      alert(message);
    }
  };

  const handleStopBatch = async (batchId: string) => {
    setStoppingBatchId(batchId);
    try {
      await api.stopBatch(batchId);
      fetchActiveBatches();
      fetchBatches();
    } catch {
      // Silently fail
    } finally {
      setStoppingBatchId(null);
    }
  };

  const handleDownloadUploadList = () => {
    if (parsedEmails.length === 0) return;
    const content = parsedEmails.join('\n');
    const blob = new Blob([content], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `emails-to-verify-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleDownloadBatch = async (batchId: string) => {
    try {
      const data = await api.getBatchResults(batchId);
      if (!data.emails || data.emails.length === 0) {
        alert('No results available');
        return;
      }

      // Create CSV content
      const headers = ['email', 'status', 'reason', 'score', 'domain', 'disposable', 'free', 'accept_all', 'role', 'verified_at'];
      const rows = data.emails.map((e: any) => [
        e.email,
        e.status,
        e.reason || '',
        e.score ?? '',
        e.domain_name || '',
        e.domain_disposable || '',
        e.domain_free || '',
        e.domain_accept_all || '',
        e.account_role || '',
        e.verified_at || '',
      ]);

      const csvContent = [
        headers.join(','),
        ...rows.map((row: string[]) => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `batch-results-${batchId.slice(0, 8)}-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      alert('Failed to download results');
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'deliverable':
        return <Badge variant="success">deliverable</Badge>;
      case 'risky':
        return <Badge variant="warning">risky</Badge>;
      case 'undeliverable':
        return <Badge variant="destructive">undeliverable</Badge>;
      case 'error':
        return <Badge variant="destructive">error</Badge>;
      default:
        return <Badge variant="secondary">unknown</Badge>;
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'deliverable':
        return <CheckCircle className="h-5 w-5 text-emerald-600" />;
      case 'risky':
        return <AlertTriangle className="h-5 w-5 text-amber-600" />;
      case 'undeliverable':
        return <XCircle className="h-5 w-5 text-red-600" />;
      default:
        return <HelpCircle className="h-5 w-5 text-muted-foreground" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Verify Emails</h1>
        <p className="text-muted-foreground">Manually verify email addresses</p>
      </div>

      {projects.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground">
              Create a project first to start verifying emails
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
            <TabsList>
              <TabsTrigger value="single">
                <MailCheck className="h-4 w-4 mr-2" />
                Single Email
              </TabsTrigger>
              <TabsTrigger value="batch">
                <FileText className="h-4 w-4 mr-2" />
                Batch Upload
              </TabsTrigger>
              <TabsTrigger value="history">
                History
              </TabsTrigger>
            </TabsList>

            <TabsContent value="single">
              <Card>
                <CardHeader>
                  <CardTitle>Verify Single Email</CardTitle>
                  <CardDescription>
                    Enter an email address to verify
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSingleVerify} className="space-y-4">
                    <div className="flex gap-4">
                      <Select value={selectedProject} onValueChange={setSelectedProject}>
                        <SelectTrigger className="w-[200px]">
                          <SelectValue placeholder="Select project" />
                        </SelectTrigger>
                        <SelectContent>
                          {projects.map((p) => (
                            <SelectItem key={p.id} value={p.id}>
                              {p.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Input
                        type="email"
                        placeholder="email@example.com"
                        value={singleEmail}
                        onChange={(e) => setSingleEmail(e.target.value)}
                        className="flex-1"
                        required
                      />
                      <Button type="submit" disabled={singleLoading || !selectedProject}>
                        {singleLoading ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Verifying...
                          </>
                        ) : (
                          <>
                            <MailCheck className="h-4 w-4 mr-2" />
                            Verify
                          </>
                        )}
                      </Button>
                    </div>
                  </form>

                  {singleResult && (
                    <div className="mt-6 p-4 rounded-lg border bg-card">
                      <div className="flex items-center gap-4">
                        {getStatusIcon(singleResult.status)}
                        <div className="flex-1">
                          <div className="font-mono text-sm">{singleResult.email}</div>
                          <div className="flex items-center gap-2 mt-1">
                            {getStatusBadge(singleResult.status)}
                            {singleResult.reason && (
                              <span className="text-sm text-muted-foreground">
                                {singleResult.reason}
                              </span>
                            )}
                            {singleResult.cached && (
                              <Badge variant="secondary">cached</Badge>
                            )}
                          </div>
                          {singleResult.error && (
                            <div className="text-sm text-destructive mt-1">
                              {singleResult.error}
                            </div>
                          )}
                        </div>
                        {singleResult.score !== undefined && (
                          <div className="text-right">
                            <div className="text-2xl font-bold">{singleResult.score}</div>
                            <div className="text-xs text-muted-foreground">score</div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="batch" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Batch Verification</CardTitle>
                  <CardDescription>
                    Upload a file with email addresses to verify
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <Select value={selectedProject} onValueChange={setSelectedProject}>
                    <SelectTrigger className="w-[300px]">
                      <SelectValue placeholder="Select project" />
                    </SelectTrigger>
                    <SelectContent>
                      {projects.map((p) => (
                        <SelectItem key={p.id} value={p.id}>
                          {p.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  {/* Step 1: Upload */}
                  {batchStep === 'upload' && (
                    <div className="space-y-4">
                      <div
                        className="border-2 border-dashed rounded-lg p-12 text-center hover:border-primary/50 transition-colors cursor-pointer"
                        onClick={() => fileInputRef.current?.click()}
                      >
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept=".txt,.csv"
                          onChange={handleFileUpload}
                          className="hidden"
                        />
                        <FileUp className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                        <p className="text-lg font-medium">Upload Email List</p>
                        <p className="text-sm text-muted-foreground mt-1">
                          Drop a .txt or .csv file here, or click to browse
                        </p>
                        <p className="text-xs text-muted-foreground mt-4">
                          Emails can be separated by newlines, commas, or semicolons
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Step 2: Ready to start */}
                  {batchStep === 'ready' && (
                    <div className="space-y-4">
                      <div className="rounded-lg border p-6">
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-4">
                            <div className="p-3 rounded-lg bg-primary/10">
                              <FileText className="h-8 w-8 text-primary" />
                            </div>
                            <div>
                              <p className="font-medium">{uploadedFile?.name}</p>
                              <p className="text-sm text-muted-foreground">
                                {((uploadedFile?.size || 0) / 1024).toFixed(1)} KB
                              </p>
                            </div>
                          </div>
                          <Button variant="ghost" size="icon" onClick={handleCancelUpload}>
                            <X className="h-4 w-4" />
                          </Button>
                        </div>

                        <div className="mt-6">
                          <div className="rounded-lg bg-muted p-4 text-center">
                            <div className="text-3xl font-bold text-primary">
                              {parsedEmails.length.toLocaleString()}
                            </div>
                            <div className="text-sm text-muted-foreground mt-1">
                              Unique Emails
                            </div>
                          </div>
                        </div>

                        <div className="mt-6 flex gap-3">
                          <Button
                            className="flex-1"
                            onClick={handleStartBatch}
                            disabled={!selectedProject || parsedEmails.length === 0}
                          >
                            <Play className="h-4 w-4 mr-2" />
                            Start Verification
                          </Button>
                          <Button variant="outline" onClick={handleDownloadUploadList}>
                            <Download className="h-4 w-4 mr-2" />
                            Download List
                          </Button>
                          <Button variant="outline" onClick={handleCancelUpload}>
                            Cancel
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="history">
              <Card>
                <CardHeader>
                  <CardTitle>Batch History</CardTitle>
                  <CardDescription>Recent batch verification jobs</CardDescription>
                </CardHeader>
                <CardContent>
                  {batches.length === 0 && activeBatches.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      No batch jobs yet
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Project</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead className="w-[200px]">Progress</TableHead>
                          <TableHead>Cache Hits</TableHead>
                          <TableHead>Created</TableHead>
                          <TableHead className="w-[100px]">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {/* Active batches first */}
                        {activeBatches.map((batch) => (
                          <TableRow key={batch.id} className="bg-muted/30">
                            <TableCell className="font-medium">{batch.projectName}</TableCell>
                            <TableCell>
                              <Badge variant="default" className="gap-1">
                                <Loader2 className="h-3 w-3 animate-spin" />
                                {batch.status}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <div className="space-y-1">
                                <Progress value={batch.progress} className="h-2" />
                                <div className="flex justify-between text-xs text-muted-foreground">
                                  <span>{batch.progress}%</span>
                                  <span>{batch.processedCount.toLocaleString()}/{batch.totalCount.toLocaleString()}</span>
                                </div>
                              </div>
                            </TableCell>
                            <TableCell>{batch.cacheHits}</TableCell>
                            <TableCell className="text-muted-foreground">
                              {new Date(batch.createdAt).toLocaleString()}
                            </TableCell>
                            <TableCell>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleStopBatch(batch.id)}
                                disabled={stoppingBatchId === batch.id}
                              >
                                {stoppingBatchId === batch.id ? (
                                  <Loader2 className="h-3 w-3 animate-spin" />
                                ) : (
                                  <Square className="h-3 w-3" />
                                )}
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                        {/* Completed batches */}
                        {batches.filter(b => b.status !== 'processing' && b.status !== 'pending' && b.status !== 'completing').map((b) => (
                          <TableRow key={b.id}>
                            <TableCell>{b.project_name}</TableCell>
                            <TableCell>
                              <Badge
                                variant={
                                  b.status === 'completed'
                                    ? 'success'
                                    : b.status === 'failed'
                                    ? 'destructive'
                                    : b.status === 'cancelled'
                                    ? 'secondary'
                                    : 'secondary'
                                }
                              >
                                {b.status}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <div className="text-sm">
                                {b.processed_count.toLocaleString()}/{b.total_count.toLocaleString()}
                              </div>
                            </TableCell>
                            <TableCell>{b.cache_hits}</TableCell>
                            <TableCell className="text-muted-foreground">
                              {new Date(b.created_at).toLocaleString()}
                            </TableCell>
                            <TableCell>
                              {b.status === 'completed' && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDownloadBatch(b.id)}
                                >
                                  <Download className="h-3 w-3" />
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </>
      )}
    </div>
  );
}
